import { z } from "zod";
import type { Operation } from "../../types";
import { crmIdParams } from "./_shared";

const stageSchema = z.object({
  id: z.number(),
  workspace_id: z.number(),
  pipeline_id: z.number(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  probability_pct: z.number(),
  is_won: z.number(),
  is_lost: z.number(),
  sort_key: z.string(),
});

const pipelineSchema = z.object({
  id: z.number(),
  workspace_id: z.number(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  is_default: z.number(),
  sort_key: z.string(),
  stages: z.array(stageSchema),
});

const list: Operation<
  void,
  void,
  void,
  { pipelines: z.infer<typeof pipelineSchema>[] }
> = {
  method: "GET",
  path: () => "/api/crm/pipelines",
  response: z.object({ pipelines: z.array(pipelineSchema) }),
};

const create: Operation<
  void,
  void,
  {
    slug: string;
    name: string;
    description?: string | null;
    is_default?: boolean;
  },
  z.infer<typeof pipelineSchema>
> = {
  method: "POST",
  path: () => "/api/crm/pipelines",
  body: z.object({
    slug: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullable().optional(),
    is_default: z.boolean().optional(),
  }),
  response: pipelineSchema,
  // list embeds stages; every pipeline/stage write stales the collection.
  invalidates: [list],
};

const stageCreate: Operation<
  { id: number },
  void,
  {
    slug: string;
    name: string;
    description?: string | null;
    probability_pct?: number;
    is_won?: boolean;
    is_lost?: boolean;
  },
  z.infer<typeof stageSchema>
> = {
  method: "POST",
  path: ({ id }) => `/api/crm/pipelines/${id}/stages`,
  params: crmIdParams,
  body: z.object({
    slug: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullable().optional(),
    probability_pct: z.number().int().min(0).max(100).optional(),
    is_won: z.boolean().optional(),
    is_lost: z.boolean().optional(),
  }),
  response: stageSchema,
  invalidates: [list],
};

const stageIdParams = z.object({
  id: z.coerce.number().int(),
  stageId: z.coerce.number().int(),
});

const update: Operation<
  { id: number },
  void,
  { name?: string; description?: string | null },
  { ok: true }
> = {
  method: "PATCH",
  path: ({ id }) => `/api/crm/pipelines/${id}`,
  params: crmIdParams,
  body: z.object({
    name: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
  }),
  response: z.object({ ok: z.literal(true) }),
  invalidates: [list],
};

const archive: Operation<{ id: number }, void, void, { ok: true }> = {
  method: "DELETE",
  path: ({ id }) => `/api/crm/pipelines/${id}`,
  params: crmIdParams,
  response: z.object({ ok: z.literal(true) }),
  invalidates: [list],
};

const setDefault: Operation<{ id: number }, void, void, { ok: true }> = {
  method: "POST",
  path: ({ id }) => `/api/crm/pipelines/${id}/set-default`,
  params: crmIdParams,
  response: z.object({ ok: z.literal(true) }),
  // flips is_default across rows — whole list family.
  invalidates: [list],
};

const stageUpdate: Operation<
  { id: number; stageId: number },
  void,
  {
    name?: string;
    description?: string | null;
    probability_pct?: number;
    is_won?: boolean;
    is_lost?: boolean;
  },
  { ok: true }
> = {
  method: "PATCH",
  path: ({ id, stageId }) => `/api/crm/pipelines/${id}/stages/${stageId}`,
  params: stageIdParams,
  body: z.object({
    name: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    probability_pct: z.number().int().min(0).max(100).optional(),
    is_won: z.boolean().optional(),
    is_lost: z.boolean().optional(),
  }),
  response: z.object({ ok: z.literal(true) }),
  invalidates: [list],
};

const stageArchive: Operation<
  { id: number; stageId: number },
  void,
  void,
  { ok: true }
> = {
  method: "DELETE",
  path: ({ id, stageId }) => `/api/crm/pipelines/${id}/stages/${stageId}`,
  params: stageIdParams,
  response: z.object({ ok: z.literal(true) }),
  invalidates: [list],
};

export const pipelines = {
  list,
  create,
  update,
  archive,
  set_default: setDefault,
  stage_create: stageCreate,
  stage_update: stageUpdate,
  stage_archive: stageArchive,
};
